import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { blogService, BlogPost, normalizeImagePath } from '../../services/blogService';
import { NOT_FOUND, useDocumentHead } from '../../seo/documentHead';

const NewsDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchArticle = async () => {
      if (!slug) return;
      try {
        setLoading(true);
        setError(null);
        const data = await blogService.fetchNewsBySlug(slug);
        setArticle(data);
      } catch (err) {
        console.error('Error fetching news article:', err);
        setError(err instanceof Error ? err.message : 'Failed to load article');
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  useDocumentHead(
    article
      ? {
          title: `${article.title} | Stamp Out Suicide`,
          description: article.excerpt || undefined,
          canonicalPath: `/news/${article.slug}`,
          image: normalizeImagePath(article.featuredImage?.path),
        }
      : error
        ? { title: NOT_FOUND.title, noindex: true }
        : null
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger" role="alert">
          {error || 'Article not found'}
        </div>
        <Link to="/news" className="btn btn-outline-primary">
          <i className="fas fa-arrow-left me-2"></i>Back to News
        </Link>
      </div>
    );
  }

  return (
    <article className="container py-5">
      {/* Featured Image */}
      {article.featuredImage && (
        <figure className="mb-4">
          <img
            src={normalizeImagePath(article.featuredImage.path)}
            alt={article.featuredImage.alt || article.title}
            className="img-fluid w-100 rounded"
            style={{ maxHeight: '500px', objectFit: 'cover' }}
          />
          {article.featuredImage.caption && (
            <figcaption className="text-muted text-center mt-2">
              {article.featuredImage.caption}
            </figcaption>
          )}
        </figure>
      )}

      {/* Header */}
      <header className="mb-4">
        <div className="mb-2">
          <span className="badge bg-warning text-dark me-2">{article.sourceName}</span>
          <span className="text-muted">{formatDate(article.publishedAt || article.createdAt)}</span>
        </div>
        <h1 className="display-4 fw-bold mb-3">{article.title}</h1>
      </header>

      {/* Content (Summary/Intro) */}
      <div className="article-content mb-4">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{article.content}</ReactMarkdown>
      </div>

      {/* Read Full Article CTA */}
      {article.externalUrl && (
        <div className="bg-light p-4 rounded mb-5 text-center">
          <p className="mb-3">Read the complete article from the original source:</p>
          <a
            href={article.externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary btn-lg"
          >
            <i className="fas fa-external-link-alt me-2"></i>
            Read Full Article on {article.sourceName}
          </a>
        </div>
      )}

      {/* Footer Actions */}
      <footer className="border-top pt-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
          <Link to="/news" className="btn btn-outline-secondary">
            <i className="fas fa-arrow-left me-2"></i>Back to News
          </Link>
          <Link to="/submit" className="btn btn-primary">
            <i className="fas fa-edit me-2"></i>Write a Note
          </Link>
        </div>
      </footer>
    </article>
  );
};

export default NewsDetailPage;
